import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useReserve } from "@/lib/useSectionNav";
import { getGatherings } from "@/data/gatherings";

const KEY = "gaia_announce_full-moon-ritual";
const EASE = [0.22, 1, 0.36, 1];

const AnnouncementModal = ({ ready }) => {
  const [show, setShow] = useState(false);
  const reserve = useReserve();
  const upcoming = getGatherings().find((g) => g.status === "upcoming");

  useEffect(() => {
    if (!ready || !upcoming) return;
    if (sessionStorage.getItem(KEY)) return;
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, [ready, upcoming]);

  if (!upcoming) return null;

  const dismiss = () => {
    sessionStorage.setItem(KEY, "1");
    setShow(false);
  };
  const onReserve = () => {
    dismiss();
    reserve(upcoming);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-gaia-charcoal/70 px-5 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          onClick={dismiss}
          data-testid="announcement-modal"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-sm bg-gaia-bg shadow-2xl"
            initial={{ opacity: 0, y: 34, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 22, scale: 0.98 }}
            transition={{ duration: 0.75, ease: EASE }}
          >
            <button
              onClick={dismiss}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/25 text-gaia-bg backdrop-blur-md transition-colors duration-300 hover:bg-black/45"
              data-testid="announcement-close"
            >
              <X size={16} strokeWidth={1.5} />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-52 md:h-full md:min-h-[420px]">
                <img src={upcoming.hero} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>

              <div className="px-8 py-9 md:px-10 md:py-12">
                <span className="font-sans text-[11px] uppercase tracking-[0.3em] text-gaia-terracotta">
                  Our next gathering
                </span>
                <h3 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-gaia-charcoal md:text-[40px]">
                  {upcoming.title}
                </h3>
                {upcoming.subtitle && (
                  <span className="mt-3 block font-sans text-[11px] uppercase tracking-[0.28em] text-gaia-charcoal/45">
                    {upcoming.subtitle}
                  </span>
                )}
                {upcoming.tagline && (
                  <p className="mt-6 font-serif text-lg font-light italic leading-relaxed text-gaia-charcoal/80 md:text-xl">
                    {upcoming.tagline}
                  </p>
                )}

                <div className="mt-7 space-y-1.5 border-t border-gaia-charcoal/10 pt-6">
                  <p className="font-sans text-xs uppercase tracking-[0.22em] text-gaia-charcoal/70">
                    {upcoming.date}{upcoming.time ? ` · ${upcoming.time}` : ""}
                  </p>
                  <p className="font-sans text-xs uppercase tracking-[0.22em] text-gaia-charcoal/70">
                    {upcoming.location}
                  </p>
                  {upcoming.contribution && (
                    <p className="pt-1 font-serif text-sm font-light italic text-gaia-charcoal/60">
                      {upcoming.contribution}
                    </p>
                  )}
                </div>

                <div className="mt-8 flex flex-col gap-4">
                  <button
                    onClick={onReserve}
                    className="group relative overflow-hidden border border-gaia-charcoal px-8 py-3.5 text-center font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-charcoal"
                    data-testid="announcement-reserve"
                  >
                    <span aria-hidden className="absolute inset-0 origin-bottom scale-y-0 bg-gaia-charcoal transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100" />
                    <span className="relative z-10 block transition-colors duration-500 group-hover:text-gaia-bg">
                      Reserve your space
                    </span>
                  </button>
                  <Link
                    to={`/gatherings/${upcoming.slug}`}
                    onClick={dismiss}
                    className="group inline-flex items-center justify-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-charcoal/60 transition-colors duration-500 hover:text-gaia-charcoal"
                    data-testid="announcement-details"
                  >
                    View details
                    <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementModal;
